import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { GlassCard, Chip, PageHead } from "@/v2/admin/ui";
import { useDept } from "./DeptShell";
import { Megaphone, Send, Sparkles, Activity, Users, Loader2 } from "lucide-react";

const QUICK = [
  "👏 أحسنتم! تقدّم إدارتنا هذا الأسبوع مصدر فخر — واصلوا التميز.",
  "🌟 كل تحدٍ تكملونه اليوم يصنع خبرة تدوم — لا تتوقفوا.",
  "🚀 بقيت خطوات قليلة لإكمال البرنامج، لنكن الإدارة الأولى!",
];

export default function DeptMotivate() {
  const { q, meta } = useDept();
  const qc = useQueryClient();
  const [target, setTarget] = useState("all");
  const [title, setTitle] = useState("رسالة تحفيزية");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [busy2, setBusy2] = useState(false);

  const { data: mine = [] } = useQuery({
    queryKey: ["dept-suggestions"],
    queryFn: async () => (await api.get("/dept/pulse/suggestions")).data,
  });

  const send = async () => {
    if (!body.trim()) return toast.error("نص الرسالة مطلوب");
    setBusy(true);
    try {
      const { data } = await api.post(`/dept/motivate?${q}`, {
        to_all: target === "all", user_id: target === "all" ? "" : target, title, body,
      });
      toast.success(`تم إرسال الرسالة إلى ${data.sent} موظفًا`);
      setBody("");
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(false); }
  };

  const suggest = async () => {
    if (!suggestion.trim()) return toast.error("اكتب نص الإنجاز");
    setBusy2(true);
    try {
      await api.post(`/dept/pulse/suggest?${q}`, { text: suggestion });
      toast.success("تم إرسال الاقتراح للمدير الأعلى للموافقة");
      setSuggestion("");
      qc.invalidateQueries({ queryKey: ["dept-suggestions"] });
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy2(false); }
  };

  const STATUS = { pending: ["قيد المراجعة", "gold"], approved: ["منشور", "emerald"], rejected: ["مرفوض", "red"] };

  return (
    <div className="space-y-5" data-testid="dept-motivate">
      <PageHead icon={Megaphone} title="التحفيز ونبض التعلّم" sub="أرسل رسائل تحفيزية لموظفي إدارتك، واقترح إنجازات لنشرها في نبض التعلّم." />

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <GlassCard className="p-5 space-y-4">
          <h3 className="font-bold flex items-center gap-2"><Send size={16} className="text-[#91cab8]" /> رسالة تحفيزية</h3>
          <div>
            <label className="v2-label">المستلم</label>
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="dept-mot-target" className="v2-input">
              <option value="all">جميع موظفي الإدارة</option>
              {(meta?.employees || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div><label className="v2-label">العنوان</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="dept-mot-title" className="v2-input" /></div>
          <div><label className="v2-label">النص</label>
            <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} data-testid="dept-mot-body"
              placeholder="اكتب رسالتك التحفيزية..." className="v2-input resize-none" /></div>
          <div className="flex flex-wrap gap-2">
            {QUICK.map((t, i) => (
              <button key={i} onClick={() => setBody(t)} data-testid={`dept-mot-quick-${i}`}
                className="text-[11px] px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[#bcc8cf] hover:text-white">
                نموذج {i + 1}
              </button>
            ))}
          </div>
          <button onClick={send} disabled={busy} data-testid="dept-mot-send" className="v2-btn w-full">
            {busy ? <><Loader2 size={16} className="animate-spin" /> جارٍ الإرسال...</> : <><Users size={16} /> إرسال الرسالة</>}
          </button>
        </GlassCard>

        <div className="space-y-6">
          <GlassCard gold className="p-5 space-y-4">
            <h3 className="font-bold flex items-center gap-2"><Sparkles size={16} className="text-[#e3c799]" /> اقتراح إنجاز لنبض التعلّم</h3>
            <p className="text-xs text-[#bcc8cf] leading-relaxed">
              اكتب إنجازًا إيجابيًا حققته إدارتك — يُرسل للمدير الأعلى للموافقة قبل ظهوره في شريط نبض التعلّم للجميع.
            </p>
            <textarea rows={3} value={suggestion} onChange={(e) => setSuggestion(e.target.value)} data-testid="dept-suggest-text"
              placeholder="مثال: 🎉 إدارة التوجيه أكملت 100 تحدٍ هذا الشهر!" className="v2-input resize-none"
              disabled={meta && meta.suggestions_enabled === false} />
            {meta && meta.suggestions_enabled === false ? (
              <p className="text-xs text-[#F7A08A]">خاصية الاقتراح معطّلة حاليًا من المدير الأعلى.</p>
            ) : (
              <button onClick={suggest} disabled={busy2} data-testid="dept-suggest-send" className="v2-btn v2-btn-sm w-full">
                {busy2 ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} إرسال الاقتراح
              </button>
            )}
          </GlassCard>

          <GlassCard className="p-5">
            <h3 className="font-bold mb-3 flex items-center gap-2"><Activity size={16} className="text-[#91cab8]" /> اقتراحاتي السابقة</h3>
            <div className="space-y-2 max-h-72 overflow-y-auto pe-1" data-testid="dept-suggestions">
              {mine.length === 0 ? <p className="text-sm text-[#bcc8cf]">لم ترسل أي اقتراح بعد.</p> : mine.map((s) => {
                const [label, tone] = STATUS[s.status] || ["قيد المراجعة", "gold"];
                return (
                  <div key={s.id} className="v2-inset p-3 flex items-start justify-between gap-3">
                    <span className="text-xs flex-1">{s.text}</span>
                    <Chip tone={tone}>{label}</Chip>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}
